import { DOSSIER_STATE } from '@/features/common/dossierState';
import { stateTone } from '@/features/common/labels';
import type { BadgeTone } from '@/features/common/types/BadgeTone';
import type { StatusTagVariant } from '@/features/common/types/StatusTagVariant';

/**
 * MUD's own `.status-tag`, in the colour stateTone gives the same state on every other screen —
 * so "Publicat" is green in the register of funding applications as it is in the approval queue.
 *
 * Lifted out of features/events/eventState.ts when the register became its second caller: a
 * feature may not import another, and two copies of "which tone is which tag" drift the moment
 * one of them is corrected. Events keeps what is genuinely its own — a cancelled event — and
 * reads the rest from here.
 *
 * <p>It is now the only one. `stateTag` (labels.ts), `statusBadgeClass` (contentWorkflow.ts) and
 * `BADGE_VARIANTS`/`badgeVariant` (dossierState.ts) each drew a status pill of this project's own
 * invention, which MUD has no equivalent of, and each kept its own copy of the state-to-colour
 * table. Three copies disagreed: ARCHIVED was green on the events table and grey on "Cererile
 * mele", and a state none of them carried was grey on the dossier screens and blue in the queue.
 * One component, one table, so a state cannot be two colours at once again.
 */
const TAG_VARIANT: Record<BadgeTone, StatusTagVariant> = {
  ok: 'success',
  warn: 'accent',
  danger: 'danger',
  neutral: 'neutral',
  info: 'info',
};

/**
 * The states whose tag colour is not the one stateTone classifies them as, and why.
 *
 * stateTone answers a second question besides the colour — the approval queue files its rows by it
 * — so a state can need one answer for where a row belongs and another for what it looks like.
 * Correcting stateTone instead would move rows between groups.
 *
 * DRAFT is the only one. stateTone has no case for it and falls back to the info blue it gives any
 * code it does not know, while a draft is nobody's decision yet — MUD's neutral.
 *
 * <p>ARCHIVED is deliberately NOT here, though the dossier pill used to have a table of its own
 * that made it grey. stateTone calls it `ok`, with its own reason recorded beside it, and the
 * events table has drawn it green from that answer since it moved to this component. An override
 * would have contradicted the classifier to change a screen that was already right.
 */
const TAG_OVERRIDES: Record<string, StatusTagVariant> = {
  [DOSSIER_STATE.draft]: 'neutral',
};

/** The tag's colour for a state code. */
export function statusTagVariant(state: string): StatusTagVariant {
  const code = state.toUpperCase();
  return TAG_OVERRIDES[code] ?? TAG_VARIANT[stateTone(code)];
}

/** The class list for a tag whose colour the caller has already decided. */
export function statusTag(variant: StatusTagVariant): string {
  return `status-tag status-tag--${variant} is-subtle`;
}

/** The class list for a state code — the common case, and what a table column wants. */
export function statusTagClass(state: string): string {
  return statusTag(statusTagVariant(state));
}
